import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Flame, Dumbbell, BarChart2, TrendingUp, Clock, Trophy, Eye } from "lucide-react";

const schemaGoals = [
  { key: "vetverlies",  label: "Vetverlies",  icon: <Flame size={14} /> },
  { key: "spieropbouw", label: "Spieropbouw", icon: <Dumbbell size={14} /> },
  { key: "onderhoud",   label: "Onderhoud",   icon: <BarChart2 size={14} /> },
  { key: "powerlift",   label: "Powerlift",   icon: <TrendingUp size={14} /> },
];

const schemaNiveaus = [
  { key: "beginner",  label: "Beginner",  basePrice: 19, desc: "Perfect voor starters. Focus op techniek, opbouw en consistente progressie.", badge: null, extraBullet: null },
  { key: "gevorderd", label: "Gevorderd", basePrice: 29, desc: "Meer volume en intensiteit. Gericht op spiergroei en krachtontwikkeling.", badge: null, extraBullet: null },
  { key: "advanced",  label: "Advanced",  basePrice: 39, desc: "Maximale performance. Zware compounds, hoge intensiteit en slimme periodisering.", badge: "Meest gekozen door ervaren sporters", extraBullet: "Geavanceerde technieken en periodisering" },
];

const schemaFrequenties = [
  { key: "2x", label: "2x per week", surcharge: 0, desc: "Efficiënt trainen met focus op herstel en balans." },
  { key: "3x", label: "3x per week", surcharge: 3, desc: "Perfecte balans tussen progressie en herstel." },
  { key: "4x", label: "4x per week", surcharge: 5, desc: "Snellere resultaten met hogere trainingsfrequentie." },
];

export default function SchemaCard() {
  const navigate = useNavigate();
  const [selectedGoal, setSelectedGoal]   = useState("vetverlies");
  const [selectedNiveau, setSelectedNiveau] = useState("beginner");
  const [selectedFreq, setSelectedFreq]   = useState("3x");

  const currentNiveau = schemaNiveaus.find(n => n.key === selectedNiveau);
  const currentFreq   = schemaFrequenties.find(f => f.key === selectedFreq);
  const totalPrice    = currentNiveau.basePrice + currentFreq.surcharge;
  const comboKey      = `${selectedGoal}-${selectedNiveau}-${selectedFreq}`;
  const baseFeatures  = ["Compleet trainingsplan", "Duidelijke opbouw per week", "PDF + mobiele versie", "Direct downloaden en starten"];

  return (
    <div data-testid="schema-card" className="product-card flex flex-col h-full">
      <div className="product-card-header">
        <div className="product-card-icon"><Dumbbell size={22} className="text-[#C9A84C]" /></div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] mb-1">Trainingsschema</p>
          <h3 className="text-2xl font-bold text-white">Schema's</h3>
          <p className="text-zinc-400 text-sm mt-1">Stel jouw schema samen</p>
        </div>
      </div>
      <div className="flex-1 flex flex-col p-6 gap-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Kies jouw doel</p>
          <div className="grid grid-cols-2 gap-2">
            {schemaGoals.map(goal => (
              <button key={goal.key} onClick={() => setSelectedGoal(goal.key)} data-testid={`schema-goal-${goal.key}`}
                className={`pill-btn flex items-center gap-1.5 justify-center ${selectedGoal === goal.key ? "pill-btn-active" : ""}`}>
                {goal.icon} {goal.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Kies jouw niveau</p>
          <div className="grid grid-cols-3 gap-2">
            {schemaNiveaus.map(niveau => (
              <button key={niveau.key} onClick={() => setSelectedNiveau(niveau.key)} data-testid={`schema-niveau-${niveau.key}`}
                className={`pill-btn flex items-center justify-center ${selectedNiveau === niveau.key ? "pill-btn-active" : ""}`}>
                {niveau.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Hoe vaak wil je trainen?</p>
          <div className="grid grid-cols-3 gap-2">
            {schemaFrequenties.map(freq => (
              <button key={freq.key} onClick={() => setSelectedFreq(freq.key)} data-testid={`schema-freq-${freq.key}`}
                className={`pill-btn flex items-center justify-center text-center leading-tight ${selectedFreq === freq.key ? "pill-btn-active" : ""}`}>
                {freq.label}
              </button>
            ))}
          </div>
        </div>
        <div className="goal-desc-box schema-dynamic-block" key={comboKey} data-testid="schema-desc-box">
          {currentNiveau.badge && (
            <div className="schema-advanced-badge mb-2"><Trophy size={11} /> {currentNiveau.badge}</div>
          )}
          <p className="text-sm text-zinc-300 leading-relaxed">{currentNiveau.desc}</p>
          <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5">
            <Clock size={11} className="text-[#C9A84C] shrink-0" /> {currentFreq.desc}
          </p>
        </div>
        <div className="space-y-2.5">
          {[...baseFeatures, ...(currentNiveau.extraBullet ? [currentNiveau.extraBullet] : [])].map(f => (
            <div key={f} className="flex items-center gap-2 text-sm text-zinc-300">
              <CheckCircle size={15} className="text-[#C9A84C] shrink-0" /> {f}
            </div>
          ))}
        </div>
        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="schema-price-block" key={`price-${selectedNiveau}-${selectedFreq}`}>
            <div className="flex items-baseline gap-2 mb-1">
              <span data-testid="schema-price" className="text-3xl font-bold text-white schema-price-animate">€{totalPrice}</span>
              <span className="text-zinc-400 text-sm">eenmalig</span>
            </div>
            {currentFreq.surcharge > 0 && (
              <p className="text-xs text-zinc-600 mb-3" data-testid="schema-price-breakdown">
                €{currentNiveau.basePrice} basis + €{currentFreq.surcharge} frequentietoeslag
              </p>
            )}
          </div>
          <p className="text-xs text-zinc-600 mb-3 flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-zinc-600 inline-block" />
            Dit is een digitaal product. Na aankoop is restitutie niet mogelijk.
          </p>
          <button onClick={() => navigate(`/bestellen?product=standaard_schema&schema=${comboKey}&prijs=${totalPrice}`)}
            data-testid="schema-cta-btn" className="btn-gold w-full py-3 font-bold text-sm uppercase tracking-wide mt-1">
            Schema kopen <ArrowRight size={15} className="inline ml-1" />
          </button>
          <button onClick={() => window.open("/account", "_self")} data-testid="schema-demo-btn"
            className="w-full mt-2 py-2.5 text-xs font-semibold text-zinc-500 hover:text-[#C9A84C] transition-colors flex items-center justify-center gap-1.5 border border-white/5 rounded-lg hover:border-[#C9A84C]/20">
            <Eye size={12} /> Inloggen om voorbeeldschema te zien
          </button>
        </div>
      </div>
    </div>
  );
}
